// Constants
import { MESSAGES_CONST } from '../../../const/messages-const'
import { MODAL_CONSTS } from '../../../const/modal-const'

// Components
import ExhibitorProfileFeesTab from './ExhibitorProfileFeesTab'
import ExhibitorProfileRegistrationTab from './ExhibitorProfileRegistrationTab'
import ExhibitorProfileTicketsTab from './ExhibitorProfileTicketsTab'

// Third party
import { ReactJSXElement } from '@emotion/react/types/jsx-namespace'

// Models
import { TEventRegisteredUsers } from '../../../models/event-registered-users/event-registered-users.interface'

// Redux
import {
  IScratch,
  resetScratchItems,
  selectScratch,
  setScratchType,
} from '../../../store/events/eventsSlice'
import { useAppDispatch, useAppSelector } from '../../../store/hooks'

// Helpers
import { AutorenewRounded } from '@mui/icons-material'
import { useContext, useEffect, useState } from 'react'
import ViewsLoader from '../../../components/loader/ViewsLoader'
import useToasterHelper from '../../../helpers/ToasterHelper'
import { IEventReviewPublish } from '../../../models/event-review-publish/event-review-publish.interface'
import { IRegistrationFeesInterface } from '../../../models/registration-fees/registrationFees.interface'
import { IRegistrationTicketInterface } from '../../../models/registration-tickets/registrationTicket.interface'
import { IRegistrationByDayInterface } from '../../../models/registrations-by-day/registrationByDay.interface'
import { IScratchPayload } from '../../../types/payment'

import { httpService } from '../../../services/httpService'
import { selectExhibitorDetails, selectRecipientR } from '../../../store/exhibitor/exhibitorSlice'
import { organizerSliceAcs } from '../../../store/organizer/organizerSlice'
import ExhibitorProfileRefundTab from './ExhibitorProfileRefundTab'
import RefundPageFooter from './footer/RefundPageFooter'
import { HandleModalContext } from '../../../layout/mainlayout/MainLayout'
import { getRegistrationEventDataInDb } from '../../../helpers/registration-on-event'
import { getScratchItems } from '../../../helpers/scratch'

// Types
type IOnRefundAmountChangeFnArgs = {
  type: 'classes' | 'tickets' | 'fees'
  itemDetails: {
    itemId: string
    itemRefundAmount: number
  }
}

export type IOnRefundAmountChangeFn = (args: IOnRefundAmountChangeFnArgs) => void

type ExhibitorProfileDisplayTabProps = {
  isRecipient?: boolean
  eventData: IEventReviewPublish | null
  isCompetitorView?: boolean
  showScratchedView: boolean
  exhibitorAllData?: TEventRegisteredUsers | null
  handleScratchView: (visible: boolean) => void
  registrationId: string
  eventId: string
}

const ExhibitorProfileDisplayTab = ({
  isRecipient,
  eventData,
  registrationId,
  eventId,
  exhibitorAllData,
  handleScratchView,
  showScratchedView,
}: ExhibitorProfileDisplayTabProps) => {
  const dispatch = useAppDispatch()
  const toastFunction = useToasterHelper()
  const recipientR = useAppSelector(selectRecipientR)
  const scratchDetails = useAppSelector(selectScratch)
  const recipient = useAppSelector(selectExhibitorDetails)

  const handleModal = useContext(HandleModalContext)?.handleModal

  const [uiLoading, setUiLoading] = useState(true)
  const [loading, setLoading] = useState<boolean>(false)
  const [listToScratch, setListToScratch] = useState<string[]>([])
  const [resetState, setresetState] = useState<boolean>(false)
  const [currentRegistrationsByDay, setCurrentRegistrationsByDay] = useState<
    IRegistrationByDayInterface[]
  >([])
  const [registrationfeesData, setRegistrationfeesData] = useState<IRegistrationFeesInterface[]>([])
  const [registrationticketData, setRegistrationTicketData] = useState<
    IRegistrationTicketInterface[]
  >([])

  useEffect(() => {
    if (currentRegistrationsByDay?.[0]) {
      const groupedByRiderId = currentRegistrationsByDay.reduce<
        Record<string, IRegistrationByDayInterface[]>
      >((acc: any, item) => {
        if (!item.riderId) return null
        if (!acc[item.riderId]) {
          acc[item.riderId] = []
        }
        acc[item.riderId].push(item)
        return acc
      }, {})

      if (groupedByRiderId) {
        const result = Object.values(groupedByRiderId)
        dispatch(organizerSliceAcs.setCurrentEntries(result))
      }
    }
  }, [currentRegistrationsByDay, dispatch])

  const updateRegistrationData = async (registrationId: string) => {
    const { registrationsByDay, registrationsFees, registrationsTickets } =
      await getRegistrationEventDataInDb({ registrationId, type: ['classes', 'fees', 'tickets'] })
    const classes = registrationsByDay.filter((rider) => !rider.isDivision)

    setCurrentRegistrationsByDay(classes)
    setRegistrationfeesData(registrationsFees)
    setRegistrationTicketData(registrationsTickets)
  }

  const loadRegistrationData = async (registrationId: string) => {
    setUiLoading(true)
    await updateRegistrationData(registrationId)
    setUiLoading(false)
  }

  useEffect(() => {
    if (registrationId) loadRegistrationData(registrationId).then()
  }, [registrationId])

  const closeScratchView = () => {
    dispatch(resetScratchItems())
    return handleScratchView(false)
  }

  useEffect(() => {
    if (!scratchDetails.scratchViewVisibility) closeScratchView()
  }, [scratchDetails.scratchViewVisibility])

  // Functions
  const onSubmitScratch = async (scratchType: IScratch['scratchType']) => {
    setLoading(true)
    try {
      const { scratchedRegistrations, scratchedFees, scratchedTickets } = getScratchItems({
        registrationsByDay: currentRegistrationsByDay.filter(
          (item) => listToScratch.includes(item.id || '') && !item.isScratched
        ),
        fees: registrationfeesData.filter(
          (item) => listToScratch.includes(item.id || '') && !item.isScratched
        ),
        tickets: registrationticketData.filter(
          (item) => listToScratch.includes(item.id || '') && !item.isScratched
        ),
        chargeSalesTax: eventData?.EventPaymentSettings?.chargeSalesTax ?? false,
      })

      if (listToScratch.length < 1) {
        return toastFunction.info({
          message: MESSAGES_CONST.NO_ITEM_SELECTED_TO_SCRATCH,
        })
      }

      const payloadData: IScratchPayload = {
        registrationDocId: registrationId,
        scratchedRegistrationByDayItems: scratchedRegistrations.scratchedItems,
        scratchedFeesItems: scratchedFees.scratchedItems,
        scratchedTicketItems: scratchedTickets.scratchedItems,
        isChargeSalesTax: eventData?.EventPaymentSettings?.chargeSalesTax ?? false,
        isAlreadyScratched: false,
      }

      await httpService({
        url: 'scratch_items',
        method: 'POST',
        data: payloadData,
      })

      const totalScratchAmount =
        scratchedRegistrations.scratchTotal +
        scratchedFees.scratchTotal +
        scratchedTickets.scratchTotal

      setresetState(!resetState)
      dispatch(setScratchType(scratchType))
      handleScratchView(false)

      handleModal?.(true, MODAL_CONSTS.EXHIBITOR_SCRATCH_CONFIRM_MODAL, {
        ...recipientR.data,
        scratchTotal: totalScratchAmount / 100,
        user: recipient,
        onSave: async () => await updateRegistrationData(registrationId),
      })
    } catch (error: any) {
      console.error(error, 'error')
      toastFunction.error({
        message: error?.response?.data?.message ?? MESSAGES_CONST.SOMETHING_WENT_WRONG,
      })
    } finally {
      setLoading(false)
    }
  }

  const onRefundAmountChange: IOnRefundAmountChangeFn = ({ itemDetails, type }) => {
    if (type === 'classes') {
      let registrationData_ = currentRegistrationsByDay.map((currentRegistrationByDay) => {
        if (currentRegistrationByDay.id === itemDetails.itemId) {
          return {
            ...currentRegistrationByDay,
            amountRefunded: itemDetails.itemRefundAmount,
          }
        } else {
          return currentRegistrationByDay
        }
      })

      setCurrentRegistrationsByDay(registrationData_)
    }

    if (type === 'fees') {
      let registrationfeesData_ = registrationfeesData.map((currRegistrationFees) => {
        if (currRegistrationFees.id === itemDetails.itemId) {
          return {
            ...currRegistrationFees,
            amountRefunded: itemDetails.itemRefundAmount,
          }
        } else {
          return currRegistrationFees
        }
      })

      setRegistrationfeesData(registrationfeesData_)
    }

    if (type === 'tickets') {
      let registrationticketData_ = registrationticketData.map((currRegistrationTicket) => {
        if (currRegistrationTicket.id === itemDetails.itemId) {
          return {
            ...currRegistrationTicket,
            amountRefunded: itemDetails.itemRefundAmount,
          }
        } else {
          return currRegistrationTicket
        }
      })
      setRegistrationTicketData(registrationticketData_)
    }
  }

  const getScratchView = (scratchType: IScratch['scratchType']): ReactJSXElement | null => {
    let disabled = false
    if (registrationfeesData.length) {
      let count = 0
      registrationfeesData.forEach((data) => {
        if (data.isScratched) {
          count++
        }
      })
      if (count === registrationfeesData.length) {
        disabled = true
      }
    } else if (registrationticketData.length) {
      let count = 0
      registrationticketData.forEach((data) => {
        if (data.isScratched) {
          count++
        }
      })
      if (count === registrationticketData.length) {
        disabled = true
      }
    } else if (currentRegistrationsByDay.length) {
      let count = 0
      currentRegistrationsByDay.forEach((data) => {
        if (data.isScratched) {
          count++
        }
      })
      if (count === currentRegistrationsByDay.length) {
        disabled = true
      }
    }

    if (!showScratchedView) return null

    return (
      <div className="w-full">
        <button
          onClick={() => onSubmitScratch(scratchType)}
          type="button"
          disabled={disabled}
          className={`${disabled ? 'opacity-40' : ''} w-full h-12 mx-auto flex items-center justify-center py-2 px-4 border border-transparent rounded-lg shadow-sm text-sm font-medium text-white bg-SeabiscuitMainThemeColor  mt-2 uppercase`}
        >
          {loading ? (
            <AutorenewRounded fontSize="small" className="animate-spin" />
          ) : (
            <>{eventData && eventData?.status === 'cancel' ? 'Submit' : 'Scratch'}</>
          )}
        </button>
        <button
          onClick={closeScratchView}
          type="button"
          className="w-full h-12 mx-auto flex items-center justify-center py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-SeabiscuitDark200ThemeColor bg-SeabiscuitLightThemeColor mt-2 uppercase"
        >
          Cancel
        </button>
      </div>
    )
  }

  if (uiLoading) {
    return (
      <div className="h-fit my-10 flex justify-center items-center">
        <ViewsLoader size="lg" color="#F70763" />
      </div>
    )
  }

  return (
    <>
      <ExhibitorProfileRegistrationTab
        isRefund={true}
        isRecipient={isRecipient}
        registrationsByDay={currentRegistrationsByDay}
        getScratchView={getScratchView}
        onRefundAmountChange={onRefundAmountChange}
        showScratchedView={showScratchedView}
        setListToScratch={setListToScratch}
        listToScratch={listToScratch}
        eventId={eventId}
        eventData={eventData}
      />
      <ExhibitorProfileFeesTab
        isRefund={true}
        isRecipient={isRecipient}
        getScratchView={getScratchView}
        exhibitorAllData={exhibitorAllData}
        showScratchedView={showScratchedView}
        setListToScratch={setListToScratch}
        onRefundAmountChange={onRefundAmountChange}
        listToScratch={listToScratch}
        registrationfeesData={registrationfeesData}
        eventData={eventData}
      />
      <ExhibitorProfileTicketsTab
        isRefund
        isRecipient={isRecipient}
        getScratchView={getScratchView}
        exhibitorAllData={exhibitorAllData}
        showScratchedView={showScratchedView}
        onRefundAmountChange={onRefundAmountChange}
        registrationticketData={registrationticketData}
        setListToScratch={setListToScratch}
        listToScratch={listToScratch}
        eventData={eventData}
      />
      <ExhibitorProfileRefundTab
        eventData={eventData}
        registrarId={exhibitorAllData?.userId || ''}
        eventId={exhibitorAllData?.eventId || ''}
      />
      <RefundPageFooter
        registrationId={registrationId}
        registrationData={currentRegistrationsByDay}
        setRegistrationData={setCurrentRegistrationsByDay}
        registrationfeesData={registrationfeesData}
        registrationticketData={registrationticketData}
        setRegistrationfeesData={setRegistrationfeesData}
        setRegistrationTicketData={setRegistrationTicketData}
        eventId={eventId}
        eventData={eventData}
      />
    </>
  )
}

export default ExhibitorProfileDisplayTab
